/**
 * Workspace API service with functions for agents, tasks, and workspace context.
 *
 * @module api/workspace
 * @see TASK-FRONTEND-WS-STATE-CHG-001
 */
import client, { getTenantId, getWorkspaceId } from "./client";
import type { ArtisAnswerModel } from "../types/artisAnswerModel";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AgentStatus = "active" | "inactive" | "archived";

/**
 * Every agent processing pattern: the core conversational type plus one per
 * plugin agent type (deploy/plugins/*.plugin.yaml `processing_pattern`). The
 * tenant Agents page finds a plugin type BY its pattern, so each plugin owns its
 * own value (PR #2368 review F1; pinned by api/processingPattern.test.ts).
 */
export const PROCESSING_PATTERNS = [
  "conversational",
  "document_processor",
  "data_extraction",
  "agentic_research",
  "translation",
] as const;

export type ProcessingPattern = (typeof PROCESSING_PATTERNS)[number];

export interface Agent {
  id: string;
  name: string;
  description: string;
  model_id: string;
  status: AgentStatus;
  workspace_id: string;
  temperature: number;
  max_tokens: number;
  top_p: number;
  top_k: number;
  system_prompt: string;
  knowledge_sources: unknown[];
  configuration?: Record<string, unknown>;
  security: Record<string, unknown>;
  processing_pattern?: ProcessingPattern;
  plugin_id?: string;
  config_locked?: boolean;
  editable_fields?: string[];
  /**
   * Present on an ARTIS agent only: the model it answers with (D-ANS-02). The
   * agent's `model_id` is not that model, so an ARTIS agent's surfaces show this
   * instead of a model picker (D-ANS-03).
   */
  artis_answer_model?: ArtisAnswerModel;
  created_at: string;
  updated_at: string;
}

export interface AgentsResponse {
  items: Agent[];
  total_count: number;
  page: number;
  per_page: number;
}

export interface AgentQueryParams {
  search?: string;
  status?: AgentStatus;
  page?: number;
  page_size?: number;
  /**
   * Narrow the list to one workspace (TASK-BUGFIX-AGENTS-WORKSPACE-SCOPE).
   * The spelling SPEC-FRONTEND-API-009 prescribes: `GET /api/v1/agents?workspace_id={id}`.
   * A VIEW filter only - the server still derives tenant from the verified
   * principal and re-checks this workspace against that tenant on every
   * request, so it can narrow the caller's own tenant list and nothing else.
   */
  workspace_id?: string;
}

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  owner_id: string;
  is_default?: boolean;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceSettings {
  default_agent_id?: string;
  theme?: "light" | "dark" | "system";
  notifications_enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Cache helpers
// ---------------------------------------------------------------------------

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const CACHE_TTL_MS = 30_000; // 30 seconds

/**
 * Scope a cache key by the ACTIVE tenant + workspace (from the api client's
 * registered getters). Every response cached here depends on the X-Tenant-ID /
 * X-Workspace-ID headers the client injects, so an unscoped key served the
 * PREVIOUS tenant's rows for up to the TTL after a tenant/workspace switch —
 * the stale cross-tenant agents list of 2026-08-24
 * (TASK-BUGFIX-FE-MODEL-BANNER-STALE-LIST, handoff item E tail).
 */
function scopedKey(key: string): string {
  return `${getTenantId() ?? ""}/${getWorkspaceId() ?? ""}:${key}`;
}

function getCached<T>(key: string): T | null {
  const entry = cache.get(scopedKey(key));
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    cache.delete(scopedKey(key));
    return null;
  }
  return entry.data as T;
}

function setCache<T>(key: string, data: T): void {
  cache.set(scopedKey(key), { data, timestamp: Date.now() });
}

/** Invalidate all workspace-related cache entries. */
export function invalidateWorkspaceCache(): void {
  cache.clear();
}

/**
 * Invalidate cache entries matching a specific prefix, within the ACTIVE
 * tenant+workspace scope (matching the scoping applied by get/setCache).
 */
export function invalidateCacheByPrefix(prefix: string): void {
  const scoped = scopedKey(prefix);
  for (const key of cache.keys()) {
    if (key.startsWith(scoped)) {
      cache.delete(key);
    }
  }
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Fetch available agents with optional filtering.
 * GET /api/v1/agents
 *
 * Pass `workspace_id` to get only that workspace's agents. Omitting it
 * returns every agent in the tenant - the pre-2026-09-14 behaviour, kept so
 * the admin SPA and the API-key callers are unaffected.
 */
export async function getAgents(params?: AgentQueryParams): Promise<AgentsResponse> {
  const cacheKey = `agents:${JSON.stringify(params ?? {})}`;
  const cached = getCached<AgentsResponse>(cacheKey);
  if (cached) return cached;

  const response = await client.get<AgentsResponse>("/agents", { params });
  setCache(cacheKey, response.data);
  return response.data;
}

/**
 * Fetch a single agent by ID.
 * GET /api/v1/agents/:id
 */
export async function getAgent(agentId: string): Promise<Agent> {
  const cacheKey = `agent:${agentId}`;
  const cached = getCached<Agent>(cacheKey);
  if (cached) return cached;

  const response = await client.get<Agent>(`/agents/${encodeURIComponent(agentId)}`);
  setCache(cacheKey, response.data);
  return response.data;
}

/**
 * Fetch the current workspace info.
 * GET /api/v1/workspace
 */
export async function getWorkspace(): Promise<Workspace> {
  const cacheKey = "workspace:current";
  const cached = getCached<Workspace>(cacheKey);
  if (cached) return cached;

  const response = await client.get<Workspace>("/workspace");
  setCache(cacheKey, response.data);
  return response.data;
}

/**
 * Fetch the list of all workspaces visible to the current tenant.
 * GET /api/v1/workspaces — backend scopes by JWT tenant_id, so this
 * returns every workspace for the active tenant.
 */
export async function listWorkspaces(): Promise<Workspace[]> {
  const cacheKey = "workspace:list";
  const cached = getCached<Workspace[]>(cacheKey);
  if (cached) return cached;

  const response = await client.get<{ items: Workspace[] | null; total_count: number }>(
    "/workspaces",
  );
  const items = response.data.items ?? [];
  setCache(cacheKey, items);
  return items;
}

/**
 * Create a new workspace for the current tenant.
 * POST /api/v1/workspaces — backend scopes by JWT tenant_id and
 * requires the workspace.settings.manage permission.
 */
export async function createWorkspace(input: {
  name: string;
  description?: string;
}): Promise<Workspace> {
  invalidateCacheByPrefix("workspace:");
  const response = await client.post<Workspace>("/workspaces", input);
  return response.data;
}

/**
 * Rename a workspace / edit its description.
 * PUT /api/v1/workspaces/{id} — requires workspace.settings.manage, the SAME
 * permission as the settings PATCH below (spec-authz-pdp.routes.yaml:266/:274).
 *
 * USE THIS, NOT `updateWorkspaceSettings`, for name/description. That endpoint's
 * handler merges ONLY `transparency_*` keys and, when none are present, skips
 * its write entirely and still answers 200 — which is why renaming a workspace
 * used to report "Settings saved successfully" while saving nothing
 * (TASK-FIX-SILENT-FIELD-DROPS). This route's handler decodes name/description
 * as POINTERS, so omitting a field leaves it untouched (partial update).
 */
export async function updateWorkspace(
  workspaceId: string,
  input: { name?: string; description?: string },
): Promise<Workspace> {
  invalidateCacheByPrefix("workspace:");
  const response = await client.put<Workspace>(
    `/workspaces/${encodeURIComponent(workspaceId)}`,
    input,
  );
  return response.data;
}

/**
 * Fetch workspace settings.
 * GET /api/v1/workspace/settings
 */
export async function getWorkspaceSettings(): Promise<WorkspaceSettings> {
  const cacheKey = "workspace:settings";
  const cached = getCached<WorkspaceSettings>(cacheKey);
  if (cached) return cached;

  const response = await client.get<WorkspaceSettings>("/workspace/settings");
  setCache(cacheKey, response.data);
  return response.data;
}

/**
 * Update workspace settings.
 * PATCH /api/v1/workspace/settings
 */
export async function updateWorkspaceSettings(
  settings: Partial<WorkspaceSettings>,
): Promise<WorkspaceSettings> {
  invalidateCacheByPrefix("workspace:");
  const response = await client.patch<WorkspaceSettings>(
    "/workspace/settings",
    settings,
  );
  return response.data;
}
